import React from 'react';
import { useQuery } from 'react-query';
import {
  Grid,
  Paper,
  Typography,
  Box,
  Card,
  CardContent,
  LinearProgress,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  People,
  Business,
  Home,
  Report,
  TrendingUp,
  TrendingDown,
} from '@mui/icons-material';
import { usersAPI, buildingsAPI, flatsAPI, complaintsAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

const StatCard = ({ title, value, icon, color, trend, isMobile }) => (
  <Card sx={{ height: '100%' }}>
    <CardContent sx={{ p: isMobile ? 2 : 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box>
          <Typography color="textSecondary" gutterBottom variant={isMobile ? "body2" : "h6"} sx={{ fontSize: isMobile ? '12px' : 'inherit' }}>
            {title}
          </Typography>
          <Typography variant={isMobile ? "h5" : "h4"} component="div" sx={{ fontSize: isMobile ? '18px' : 'inherit' }}>
            {value}
          </Typography>
          {trend && (
            <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
              {trend > 0 ? (
                <TrendingUp color="success" fontSize="small" />
              ) : (
                <TrendingDown color="error" fontSize="small" />
              )}
              <Typography
                variant="body2"
                color={trend > 0 ? 'success.main' : 'error.main'}
                sx={{ ml: 0.5, fontSize: isMobile ? '10px' : '12px' }}
              >
                {Math.abs(trend)}% from last month
              </Typography>
            </Box>
          )}
        </Box>
        <Box
          sx={{
            backgroundColor: color,
            borderRadius: '50%',
            width: isMobile ? 40 : 56,
            height: isMobile ? 40 : 56,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {React.cloneElement(icon, { sx: { color: 'white', fontSize: isMobile ? 20 : 24 } })}
        </Box>
      </Box>
    </CardContent>
  </Card>
);

// Helper to ensure value is always an array
const safeArray = (val) => Array.isArray(val) ? val : [];

const Dashboard = () => {
  const { user } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  // Updated allowed groups to include building_admin
  const allowedGroups = ["admin", "maintenance_staff"];
  const userGroups = user?.groups || [];
  
  // Extract group names - handle both old format (strings) and new format (objects)
  const groupNames = userGroups.map(g => typeof g === 'string' ? g : g.name);
  
  // Check both groups and direct role property
  const hasAccess = groupNames.some(g => allowedGroups.includes(g)) || 
                   allowedGroups.includes(user?.role);

  const { data: users = [], isLoading: isUsersLoading } = useQuery('users', async () => (await usersAPI.getAll()).data);
  const { data: buildings = [], isLoading: isBuildingsLoading } = useQuery('buildings', async () => (await buildingsAPI.getAll()).data);
  const { data: flats = [], isLoading: isFlatsLoading } = useQuery('flats', async () => (await flatsAPI.getAll()).data);
  
  const { data: complaints = [], isLoading: isComplaintsLoading} = useQuery('complaints', async () => (await complaintsAPI.getAll()).data);

  const usersSafe = safeArray(users);
  const buildingsSafe = safeArray(buildings);
  const flatsSafe = safeArray(flats);
  const complaintsSafe = safeArray(complaints);

  if (!hasAccess) {
    return (
      <Box sx={{ p: 4 }}>
        <Typography variant="h4" color="error" gutterBottom>Not Authorized</Typography>
        <Typography>You do not have access to view the Dashboard.</Typography>
      </Box>
    );
  }

  const complaintStatusData = [
    { name: 'Open', value: complaintsSafe.filter(c => c.status === 'OPEN').length },
    { name: 'In Progress', value: complaintsSafe.filter(c => c.status === 'IN_PROGRESS').length },
    { name: 'Resolved', value: complaintsSafe.filter(c => c.status === 'RESOLVED').length },
    { name: 'Closed', value: complaintsSafe.filter(c => c.status === 'CLOSED').length },
  ];

  return (
    <Box sx={{ p: isMobile ? 1 : 0 }}>
      <Typography variant={isMobile ? "h5" : "h4"} gutterBottom sx={{ fontSize: isMobile ? '20px' : 'inherit' }}>
        Dashboard
      </Typography>
      
      <Grid container spacing={isMobile ? 2 : 3}>
        {/* Statistics Cards */}
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Users"
            value={usersSafe.length}
            icon={<People />}
            color="#1976d2"
            trend={5.2}
            isMobile={isMobile}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Buildings"
            value={buildingsSafe.length}
            icon={<Business />}
            color="#388e3c"
            trend={2.1}
            isMobile={isMobile}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Flats"
            value={flatsSafe.length}
            icon={<Home />}
            color="#f57c00"
            trend={-1.5}
            isMobile={isMobile}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Active Complaints"
            value={complaintsSafe.length}
            icon={<Report />}
            color="#d32f2f"
            trend={8.7}
            isMobile={isMobile}
          />
        </Grid>

        {/* Complaint Status */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: isMobile ? 2 : 3 }}>
            <Typography variant={isMobile ? "body1" : "h6"} gutterBottom sx={{ fontSize: isMobile ? '16px' : 'inherit' }}>
              Complaint Status Distribution
            </Typography>
            <Box sx={{ mt: 2 }}>
              {complaintStatusData.map((item) => (
                <Box key={item.name} sx={{ mb: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2" sx={{ fontSize: isMobile ? '12px' : '14px' }}>{item.name}</Typography>
                    <Typography variant="body2" sx={{ fontSize: isMobile ? '12px' : '14px' }}>{item.value}</Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={(item.value / (complaintsSafe || []).length) * 100}
                    sx={{ height: isMobile ? 6 : 8, borderRadius: 4 }}
                  />
                </Box>
              ))}
            </Box>
          </Paper>
        </Grid>

        {/* Recent Activity */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: isMobile ? 2 : 3 }}>
            <Typography variant={isMobile ? "body1" : "h6"} gutterBottom sx={{ fontSize: isMobile ? '16px' : 'inherit' }}>
              Recent Activity
            </Typography>
            <Box sx={{ mt: 2 }}>
              {complaintsSafe.slice(0, 5).map((complaint) => (
                <Box key={complaint.complaintId} sx={{ mb: 2, p: isMobile ? 1.5 : 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                  <Typography variant="subtitle2" sx={{ fontSize: isMobile ? '13px' : '14px' }}>{complaint.title}</Typography>
                  <Typography variant="body2" color="textSecondary" sx={{ fontSize: isMobile ? '11px' : '12px' }}>
                    Status: {complaint.status} | Priority: {complaint.priority}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Dashboard; 
